/**
 * Generates a task path for a given pull request global ID.
 * @param globalId - The global ID of the pull request.
 * @param sessionId - The ID of the session to be focused on page load.
 * @returns The task path URL, or an empty string if globalId is empty.
 */
export const generateTaskPath = (globalId: string, sessionId?: string) => {
  if (!globalId) {
    return ''
  }

  let taskPath = `/copilot/tasks/pull/${encodeURIComponent(globalId)}`
  if (sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

export interface GenerateTaskPathForRepoOptions {
  /** The repository's name with owner (e.g., "owner/repo"). */
  repositoryNwo?: string
  /** The task ID for direct task linking. */
  taskId?: string
  /** The pull request number. */
  pullRequestNumber?: number
  /** The global ID of the pull request. */
  globalId?: string
  /** The ID of the session to be focused on page load. */
  sessionId?: string
  /** Whether to link to the repository-based task view. */
  linkToSessionsInRepo?: boolean
  /** When true, generates /copilot/t/{nwo}/{taskId} URLs. Takes precedence over other URL types. */
  isUsingIntentDetection?: boolean
}

/**
 * Generates a task path for a given pull request global ID within a specific repository context.
 * @param options - The options for generating the task path.
 * @returns The task path URL, or an empty string if globalId is empty.
 */
export const generateTaskPathForRepo = ({
  globalId,
  repositoryNwo,
  pullRequestNumber,
  sessionId,
  linkToSessionsInRepo = false,
  taskId,
  isUsingIntentDetection = false,
}: GenerateTaskPathForRepoOptions) => {
  // Intent detection URLs take precedence over all other URL types
  if (isUsingIntentDetection && repositoryNwo && taskId) {
    return `/copilot/t/${repositoryNwo}/${taskId}`
  }

  let taskPath = ''
  if (repositoryNwo && linkToSessionsInRepo) {
    if (taskId) {
      taskPath = `/${repositoryNwo}/tasks/${encodeURIComponent(taskId)}`
    } else if (pullRequestNumber) {
      taskPath = `/${repositoryNwo}/agents/pull/${encodeURIComponent(pullRequestNumber)}`
    }
  }

  // Fall back to globalId path if we couldn't generate a repo-based path
  if (!taskPath && globalId) {
    taskPath = `/copilot/tasks/pull/${encodeURIComponent(globalId)}`
  }

  if (sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

/**
 * Signifies if we should link to the repository-based task view instead of the hyperspace view.
 * @returns True if the repository-based task view should be used, false otherwise.
 */
export const shouldLinkToRepoTaskView = (linkToSessionsInRepo: boolean, linkToSessionsInHyperspace: boolean) => {
  return linkToSessionsInRepo && !linkToSessionsInHyperspace
}
